import React, {useEffect, useState} from "react";
import { useSelector } from "react-redux";
import { useTranslation } from 'react-i18next';

import { Button, TextField, Grid, Typography, useTheme } from '@mui/material';
import { AddCircle, ContentCopy, Delete, OpenInBrowser } from "@mui/icons-material";
import Download from '@mui/icons-material/DownloadForOffline';

import apiClient from "../../../../../services/api";
import store from "../../../../../store";
import { toggleMessageOpen } from "../../../../../store/actions/Message";
import { createConfirmAction } from "../../../../../store/actions/ConfirmDialog";
import { setStoreData } from "../../../../../store/actions/Store";

export default function PublicTab()
{
    const { t } = useTranslation();
    const theme = useTheme();

    const storeData = useSelector((state) => state.store.storeData);
    const [floorPlan, setFloorPlan] = useState(null)
    
    useEffect(() => {
        if(storeData) {
            setFloorPlan(storeData.floor_plan);
        }
    }, [storeData])

    const handleCreateLink = () => {
        apiClient.post(
            '/api/easy-floor/create-public-url',
            {
                id: floorPlan.floor_plan_id,
                floorPlanVersionID: floorPlan.id,
                origin: window.location.origin
            }
        ).then( response => {
            const floorPlan = { ...response.data };

            floorPlan.floor_plan.width = response.data.floor_plan.width * response.data.floor_plan.scale;
            floorPlan.floor_plan.depth = response.data.floor_plan.depth * response.data.floor_plan.scale;
            floorPlan.texture.width = response.data.texture.width * response.data.floor_plan.scale / 10;
            floorPlan.texture.depth = response.data.texture.height * response.data.floor_plan.scale / 10;

            store.dispatch(setStoreData(floorPlan));
        }).catch( error => store.dispatch(toggleMessageOpen(error, "error")) );
    }

    const handleCopy = (url) => {
        navigator.clipboard.writeText(url);

        store.dispatch(toggleMessageOpen(t('Text copied to clipboard'), "success"));
    }

    const handleOpen = (url) => { window.open(url) }

    const handleDelete = () => {
        apiClient.post(
            '/api/easy-floor/delete-public-url',
            {
                id: floorPlan.floor_plan_id,
                floorPlanVersionID: floorPlan.id
            }
        ).then(response => {
            const floorPlan = { ...response.data };

            floorPlan.floor_plan.width = response.data.floor_plan.width * response.data.floor_plan.scale;
            floorPlan.floor_plan.depth = response.data.floor_plan.depth * response.data.floor_plan.scale;
            floorPlan.texture.width = response.data.texture.width * response.data.floor_plan.scale / 10;
            floorPlan.texture.depth = response.data.texture.height * response.data.floor_plan.scale / 10;

            store.dispatch(setStoreData(floorPlan));
        }).catch(error => store.dispatch(toggleMessageOpen(error, "error")));
    }

    const handleDownloadQrCode = () => {
        const image = document.getElementById('qr-code');
        let anchor = document.createElement('a');

        anchor.setAttribute('href', image.getAttribute('src'));
        anchor.setAttribute('download', 'qr-code');
        document.body.appendChild(anchor);

        anchor.click();
        anchor.remove();
    }

    const renderShareData = () => {
        if(!floorPlan || !floorPlan.public_view_token) {
            return;
        }

        const url = window.location.origin + '/public/easy-floor/view/' + (floorPlan.public_view_token !== null ? floorPlan.public_view_token : '');

        return (
            <div style={{marginTop: '10px', display: (floorPlan.public_view_token === null ? 'none' : 'block')}}>
                <Typography style={{fontWeight: 'bold'}}>
                    {t('Created public link')}
                </Typography>
                <TextField
                    multiline
                    fullWidth
                    value={url}
                    readonly
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                                borderColor: theme.palette.border.primary,
                            },
                            '&:hover fieldset': {
                                borderColor: theme.palette.border.secondary,
                            },
                            '&.Mui-focused fieldset': {
                                borderColor: theme.palette.border.secondary,
                            }
                        }
                    }}
                />
                <Grid container spacing={2} style={{marginTop: '10px', marginBottom: '10px'}}>
                    <Grid item xs={4}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={() => {handleCopy(url)}}
                            startIcon={<ContentCopy/>}
                        >   
                            {t('Copy')}
                        </Button>
                    </Grid>
                    <Grid item xs={4}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={() => {handleOpen(url)}}
                            startIcon={<OpenInBrowser/>}
                        >
                            {t('Open')}
                        </Button>
                    </Grid>
                    <Grid item xs={4}>
                        <Button
                            fullWidth
                            variant="contained"
                            size="small"
                            onClick={() => store.dispatch(createConfirmAction(handleDelete, t('Are you sure?')))}
                            startIcon={<Delete/>}
                        >
                            {t('Delete')}
                        </Button>
                    </Grid>
                </Grid>
                <div style={{textAlign: 'center'}}>
                    <div className="qr-code-container" onClick={handleDownloadQrCode}>
                        <img src={floorPlan.qr_code} id="qr-code" style={{width: '200px'}} alt="" />
                        <div className="qr-code-download">
                            <Download style={{color: 'green', position: 'absolute', top: 'calc(50% - 12px)', right: 'calc(50% - 14px)'}}/>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <React.Fragment>
            {
                (!floorPlan || !floorPlan.public_view_token)
                ? <Button
                    variant="contained"
                    onClick={handleCreateLink}
                    fullWidth
                    size='small'
                    startIcon={<AddCircle />}
                >
                    {t('Create Public Link')}
                </Button>
                : renderShareData()
            }
        </React.Fragment>
    )
}